import Image from "next/image";
import React from "react";
import Bear from "src/assets/images/bear-market.png";
import Bull from "src/assets/images/bull-market.png";
import {
  buildSectorsScaleGradient,
  type SectorColorStyle,
} from "src/helpers/api/sharedFunction";

interface SectorMomentumColorScaleProps {
  sectors: string[];
  sectorColors: Record<string, SectorColorStyle>;
  selectedSector?: string;
  highlightedSectors?: string[];
  compact?: boolean;
}

const SectorMomentumColorScale: React.FC<SectorMomentumColorScaleProps> = ({
  sectors,
  sectorColors,
  selectedSector = "",
  highlightedSectors = [],
  compact = false,
}) => {
  if (sectors.length === 0) return null;

  const scaleGradient = buildSectorsScaleGradient(sectors, sectorColors);
  const selectedIndex = selectedSector ? sectors.indexOf(selectedSector) : -1;

  const showPicker = Boolean(selectedSector && selectedIndex >= 0);
  const markerLeft =
    showPicker && sectors.length > 1
      ? (selectedIndex / (sectors.length - 1)) * 100
      : showPicker
        ? 0
        : null;

  const markerColor = showPicker
    ? sectorColors[selectedSector]?.borderColor
    : undefined;

  const highlightedMarkers = highlightedSectors
    .map((sector) => {
      const index = sectors.indexOf(sector);
      if (index < 0) return null;
      const left =
        sectors.length > 1 ? (index / (sectors.length - 1)) * 100 : 0;
      const color = sectorColors[sector]?.borderColor;
      if (!color) return null;
      return { sector, left, color };
    })
    .filter(Boolean) as { sector: string; left: number; color: string }[];

  const renderMarkerDot = (
    color: string,
    variant: "highlight" | "picker",
    key: string,
    left: number,
    sizeClass: string,
  ) => (
    <div
      key={key}
      className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 ${
        variant === "picker" ? "z-20" : "z-10"
      }`}
      style={{ left: `${left}%` }}
    >
      <div
        className={`rounded-full ${sizeClass} ring-[1.5px] ring-white ring-offset-[0.5px] ring-offset-gray-900/70 dark:ring-gray-200 dark:ring-offset-gray-950`}
        style={{
          backgroundColor: color,
          boxShadow: `0 0 0 1px rgba(0,0,0,0.7), 0 0 6px ${color}`,
        }}
      />
    </div>
  );

  const renderMarkers = (markerSize: string, pickerSize: string) => (
    <>
      {highlightedMarkers.map(({ sector, left, color }) =>
        renderMarkerDot(color, "highlight", sector, left, markerSize),
      )}
      {showPicker && markerColor && markerLeft !== null
        ? renderMarkerDot(
            markerColor,
            "picker",
            "picker",
            markerLeft,
            pickerSize,
          )
        : null}
    </>
  );

  const renderBar = (
    heightClass: string,
    markerSize: string,
    pickerSize: string,
  ) => (
    <div className={`relative ${heightClass} min-w-0 flex-1`}>
      <div className="absolute inset-0 overflow-hidden rounded-full border border-black/10 bg-black/5 shadow-inner dark:border-white/15 dark:bg-white/5">
        <div
          className="h-full w-full"
          style={{ backgroundImage: scaleGradient }}
        />
      </div>
      {renderMarkers(markerSize, pickerSize)}
    </div>
  );

  if (compact) {
    return (
      <div className="flex w-full min-w-0 items-center gap-0.5 max-[1708px]:gap-0.5 min-[1709px]:gap-1">
        <Image
          src={Bull}
          alt="Bull"
          width={14}
          height={14}
          className="mr-2 h-3.5 w-3.5 shrink-0 opacity-80 max-[1708px]:h-3 max-[1708px]:w-3 min-[1709px]:h-[18px] min-[1709px]:w-[18px]"
        />
        {renderBar(
          "h-1.5 max-[1708px]:h-1.5 min-[1709px]:h-2",
          "h-2.5 w-2.5",
          "h-2.5 w-2.5",
        )}
        <Image
          src={Bear}
          alt="Bear"
          width={14}
          height={14}
          className="ml-2 h-3.5 w-3.5 shrink-0 opacity-80 max-[1708px]:h-3 max-[1708px]:w-3 min-[1709px]:h-[18px] min-[1709px]:w-[18px]"
        />
      </div>
    );
  }

  return (
    <div className="w-[350px] shrink-0 self-center sm:w-[350px]">
      <div className="mb-1 flex items-center justify-between px-0.5 leading-none">
        <Image src={Bull} alt="Bull" width={20} height={20} />
        <Image src={Bear} alt="Bear" width={20} height={20} />
      </div>
      <div className="relative h-2 sm:h-2">
        {renderBar("h-full", "h-3 w-3", "h-3 w-3")}
      </div>
    </div>
  );
};

export default React.memo(SectorMomentumColorScale);

interface SectorMomentumLegendProps {
  sectors: string[];
  selectedSectors: string[];
  sectorColors: Record<string, SectorColorStyle>;
  onToggle: (sector: string) => void;
  compact?: boolean;
}

export const SectorMomentumLegend: React.FC<SectorMomentumLegendProps> = ({
  sectors,
  selectedSectors,
  sectorColors,
  onToggle,
  compact = false,
}) => {
  if (sectors.length === 0) return null;

  return (
    <div
      className={`flex items-center overflow-hidden ${
        compact ? "flex-nowrap gap-x-2" : "flex-wrap gap-x-3 gap-y-1"
      }`}
    >
      {sectors.map((sector) => {
        const isSelected = selectedSectors.includes(sector);
        const color =
          sectorColors[sector]?.borderColor ??
          sectorColors[sector]?.bgSolid ??
          "#999999";

        return (
          <button
            key={sector}
            type="button"
            onClick={() => onToggle(sector)}
            title={`${isSelected ? "Deselect" : "Select"} ${sector}`}
            className={`inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 transition-opacity duration-150 ${
              compact ? "text-[10px] max-[1708px]:text-[9px]" : "text-xs"
            } ${isSelected ? "opacity-100" : "opacity-35 hover:opacity-55"}`}
          >
            <span className="relative inline-flex h-2.5 w-5 shrink-0 items-center">
              <span
                className="absolute inset-x-0 h-px rounded-full"
                style={{ backgroundColor: color }}
              />
              <span
                className="relative mx-auto h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: color }}
              />
            </span>
            <span className="whitespace-nowrap text-gray-600 dark:text-gray-300">
              {sector.replace(/^Nifty\s+/i, "")}
            </span>
          </button>
        );
      })}
    </div>
  );
};

interface SectorMomentumHeaderProps {
  orderedSectors: string[];
  legendSectors: string[];
  selectedSectors: string[];
  sectorColors: Record<string, SectorColorStyle>;
  onToggleSector: (sector: string) => void;
  compact?: boolean;
}

export const SectorMomentumHeader: React.FC<SectorMomentumHeaderProps> = ({
  orderedSectors,
  legendSectors,
  selectedSectors,
  sectorColors,
  onToggleSector,
  compact = true,
}) => {
  if (orderedSectors.length === 0) return null;

  const scaleSelectedSector =
    selectedSectors.length === 1 ? selectedSectors[0] : "";

  return (
    <div className="flex w-full min-w-0 items-center gap-2 max-[1708px]:gap-1.5 min-[1709px]:gap-4">
      {legendSectors.length > 0 ? (
        <div className="min-w-0 flex-1 overflow-hidden">
          <SectorMomentumLegend
            sectors={legendSectors}
            selectedSectors={selectedSectors}
            sectorColors={sectorColors}
            onToggle={onToggleSector}
            compact={compact}
          />
        </div>
      ) : (
        <span className="min-w-0 flex-1" />
      )}

      <div className="w-[88px] shrink-0 max-[1708px]:w-[88px] min-[1709px]:w-[140px] xl:w-[180px]">
        <SectorMomentumColorScale
          compact={compact}
          sectors={orderedSectors}
          sectorColors={sectorColors}
          selectedSector={scaleSelectedSector}
          highlightedSectors={selectedSectors}
        />
      </div>
    </div>
  );
};
